import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Card, Typography, Button, message, Row, Col, Statistic, DatePicker, Tag, Segmented, Space, Tooltip } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../shared/api/AllApi';
import { formatCurrency, toApiNumber } from '../../../utils/formatters';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title, Text } = Typography;

const isCancelledOrder = (order) =>
  order?.status === 'cancelled' || Number(order?.payment_status) === 4;

const paymentStatusTag = (order, t) => {
  if (isCancelledOrder(order)) {
    return <Tag color="red">{t('cancelled', 'Cancelled')}</Tag>;
  }
  const ps = Number(order?.payment_status);
  if (ps === 3 || toApiNumber(order?.outstanding_balance, 0) <= 0.0001) {
    return <Tag color="green">{t('fullyPaid', 'Paid')}</Tag>;
  }
  if (ps === 2 || toApiNumber(order?.amount_paid, 0) > 0) {
    return <Tag color="orange">{t('partiallyPaid', 'Partial')}</Tag>;
  }
  return <Tag color="volcano">{t('unpaid', 'Unpaid')}</Tag>;
};

const CustomerOrderHistory = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [statusFilter, setStatusFilter] = useState('active');

  const handleDateChange = async (dates) => {
    setDateRange(dates);

    if (dates && dates.length === 2) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      try {
        await fetchCustomerOrders(start_date, end_date);
      } catch (error) {
        console.error('Error fetching filtered orders:', error);
      }
    } else {
      fetchCustomerOrders();
    }
  };

  const fetchCustomerOrders = useCallback(async (start_date, end_date) => {
    try {
      const response = await apiClient.get(`/customers/${id}/orders`, {
        params: { start_date, end_date },
      });
      setOrders(response.data.orders || []);
      setCustomerInfo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error(t('fetchError'));
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchCustomerOrders();
  }, [fetchCustomerOrders]);

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: <b>{t('ژمارەی داواکاری')}</b>,
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: <b>{t('بەرواری داواکاری')}</b>,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => (text ? dayjs(text).utc().format('DD/MM/YYYY HH:mm') : ''),
    },
    {
      title: <b>{t('status', 'Status')}</b>,
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Tooltip title={record.cancellation_reason || undefined}>
          {paymentStatusTag(record, t)}
        </Tooltip>
      ),
    },
    {
      title: <b>{t('کێشی داواکاری')}</b>,
      dataIndex: 'total_order_weight',
      key: 'total_order_weight',
      render: (text) => {
        const raw = parseFloat((text || '0').toString().replace(/,/g, ''));
        return `${raw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg`;
      },
    },
    {
      title: <b>{t('کۆی گشتی بڕی')}</b>,
      dataIndex: 'total_price',
      key: 'total_price',
      render: (text, record) => (
        <span style={{ textDecoration: isCancelledOrder(record) ? 'line-through' : 'none', opacity: isCancelledOrder(record) ? 0.65 : 1 }}>
          {formatCurrency(text)}
        </span>
      ),
    },
    {
      title: <b>{t('بڕی پارە دراو')}</b>,
      dataIndex: 'amount_paid',
      key: 'amount_paid',
      render: (text) => formatCurrency(text),
    },
    {
      title: <b>{t('باڵانسی نەدراو')}</b>,
      dataIndex: 'outstanding_balance',
      key: 'outstanding_balance',
      render: (text, record) => {
        if (isCancelledOrder(record)) return <Tag>{t('cancelled', 'Cancelled')}</Tag>;
        const owed = toApiNumber(text, 0);
        return (
          <span style={{ fontWeight: 700, color: owed > 0.0001 ? '#cf1322' : '#389e0d' }}>
            {formatCurrency(owed)}
          </span>
        );
      },
    },
    {
      title: <b>{t('کردارەکان')}</b>,
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Link to={`/customer/${id}/order/${record.id}`}>
            <Button type="primary">{t('viewSale', 'View sale')}</Button>
          </Link>
          <Link to={`/TableOrder/InfoOrder/${record.id}`} target="_blank">
            <Button icon={<PrinterOutlined />}>{t('print', 'Print')}</Button>
          </Link>
        </Space>
      ),
    },
  ];

  const safeDateRange = Array.isArray(dateRange) ? dateRange : [null, null];

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (safeDateRange[0] && safeDateRange[1]) {
        const orderDate = dayjs(order.created_at);
        if (
          !(orderDate.isAfter(safeDateRange[0].startOf('day').subtract(1, 'ms')) &&
            orderDate.isBefore(safeDateRange[1].endOf('day').add(1, 'ms')))
        ) {
          return false;
        }
      }
      const cancelled = isCancelledOrder(order);
      const outstanding = toApiNumber(order.outstanding_balance, 0);
      if (statusFilter === 'active' && cancelled) return false;
      if (statusFilter === 'cancelled' && !cancelled) return false;
      if (statusFilter === 'unpaid' && (cancelled || outstanding <= 0.0001)) return false;
      return true;
    });
  }, [orders, safeDateRange, statusFilter]);

  const counts = useMemo(() => {
    const cancelled = orders.filter(isCancelledOrder);
    const active = orders.filter((o) => !isCancelledOrder(o));
    const unpaid = active.filter((o) => toApiNumber(o.outstanding_balance, 0) > 0.0001);
    return {
      all: orders.length,
      active: active.length,
      cancelled: cancelled.length,
      unpaid: unpaid.length,
    };
  }, [orders]);

  const activeForStats = filteredOrders.filter((o) => !isCancelledOrder(o));
  const statsTotalPrice = activeForStats.reduce((sum, order) => sum + toApiNumber(order.total_price, 0), 0);
  const statsTotalOutstanding = activeForStats.reduce((sum, order) => sum + toApiNumber(order.outstanding_balance, 0), 0);
  const statsTotalPaid = activeForStats.reduce((sum, order) => sum + toApiNumber(order.amount_paid, 0), 0);
  const statsTotalWeight = activeForStats.reduce((sum, order) => sum + toApiNumber(order.total_order_weight, 0), 0);

  if (!id) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Invalid or Missing Customer ID</h1>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f8fafc',
          borderRadius: 12,
          padding: 20,
          border: '1px solid #e2e8f0',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {customerInfo ? customerInfo.name : t('orderHistory')}
            </Title>
            <Text style={{ color: '#475569' }}>
              {t(
                'customerSalesClearHint',
                'This is this customer’s sales list. Use the buttons below to show only unpaid or cancelled sales.'
              )}
            </Text>
            {customerInfo && (
              <div style={{ marginTop: 8, fontSize: 16, fontWeight: 700, color: '#b91c1c' }}>
                {t('whatTheyOwe', 'What they owe now')}: {formatCurrency(toApiNumber(customerInfo.total_owed, 0))}
              </div>
            )}
          </div>
          <Link to="/GetCustomer">
            <Button type="primary" icon={<ArrowLeftOutlined />}>
              {t('backToCustomers', 'Back to customers')}
            </Button>
          </Link>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ background: '#f6ffed', borderRadius: 10 }}>
            <Statistic title={t('activeOrders', 'Active sales')} value={counts.active} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card style={{ background: '#e6f7ff', borderRadius: 10 }}>
            <Statistic title={t('کۆی گشتی بڕی')} value={formatCurrency(statsTotalPrice)} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card style={{ background: '#d9f7be', borderRadius: 10 }}>
            <Statistic title={t('کۆی گشتی پارەی پێدراوە')} value={formatCurrency(statsTotalPaid)} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card style={{ background: '#fff1f0', borderRadius: 10 }}>
            <Statistic
              title={t('کۆی گشتی قەرزی نەدراو')}
              value={formatCurrency(toApiNumber(customerInfo?.total_owed, statsTotalOutstanding))}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card style={{ background: '#fff7e6', borderRadius: 10 }}>
            <Statistic title={t('cancelled', 'Cancelled')} value={counts.cancelled} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Segmented
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { label: `${t('showActiveSales', 'Show active sales')} (${counts.active})`, value: 'active' },
            { label: `${t('showUnpaidOnly', 'Show unpaid only')} (${counts.unpaid})`, value: 'unpaid' },
            { label: `${t('showCancelledSales', 'Show cancelled')} (${counts.cancelled})`, value: 'cancelled' },
            { label: `${t('showAllSales', 'Show all')} (${counts.all})`, value: 'all' },
          ]}
        />
        <div style={{ direction: 'ltr' }}>
          <DatePicker.RangePicker
            value={safeDateRange}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            style={{ minWidth: 260 }}
            allowClear
            placeholder={[t('start Date'), t('end Date')]}
            inputReadOnly
          />
        </div>
      </Space>

      <Card
        style={{
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Table
          dataSource={filteredOrders}
          columns={columns}
          pagination={{ pageSize: 50 }}
          loading={loading}
          rowKey={(record) => record.id}
          rowClassName={(record) => (isCancelledOrder(record) ? 'row-cancelled' : '')}
          locale={{ emptyText: t('noDataFound') }}
        />
      </Card>
    </div>
  );
};

export default CustomerOrderHistory;
